// Profile Page Component
// LazySEM Frontend - User profile page

import React, { useState, useEffect } from 'react';
import { FiUser, FiActivity, FiAlertCircle, FiEdit, FiSave, FiX, FiRefreshCw, FiPhone } from 'react-icons/fi';
import { authAPI } from '../../api/auth';
import { organizationAPI } from '../../api/organizations';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/ui/Input/Input';
import { Badge } from '../../components/ui/Badge/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import type { UserActivity } from '../../types/activity';
import { getActivityIcon, getActivityColor, getActivityCategory } from '../../types/activity';
import styles from './Profile.module.css';

import { logger } from '../../utils/logger';

interface ProfileData {
  id: string;
  email: string;
  personal_info: {
    first_name: string;
    last_name: string;
    full_name: string;
    avatar?: string;
    bio?: string;
    location?: string;
    phone?: string;
    phone_verified: boolean;
    timezone: string;
  };
  professional_info: {
    job_title?: string;
    company?: string;
    industry?: string;
    experience_level?: string;
    skills: string[];
    certifications: string[];
  };
  social_links: {
    linkedin?: string;
    twitter?: string;
    website?: string;
    github?: string;
  };
  preferences: {
    language: string;
    date_format: string;
    time_format: string;
    theme: string;
  };
  privacy: {
    profile_visibility: string;
    allow_marketing_emails: boolean;
    allow_data_sharing: boolean;
  };
  account_info: {
    is_active: boolean;
    is_verified: boolean;
    last_login: string;
    created_at: string;
    updated_at: string;
  };
  organizations: any[];
}

interface EditFormData {
  first_name: string;
  last_name: string;
  phone_number: string;
  timezone: string;
  bio: string;
  location: string;
  job_title: string;
  company: string;
  industry: string;
  experience_level: string;
  skills: string;
}

const Profile: React.FC = () => {
  const { /* user */ } = useAuth(); // user not used
  const { success: showSuccess, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState<'overview' | 'activity'>('overview');
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data for editing
  const [formData, setFormData] = useState<EditFormData>({
    first_name: '',
    last_name: '',
    phone_number: '',
    timezone: '',
    bio: '',
    location: '',
    job_title: '',
    company: '',
    industry: '',
    experience_level: '',
    skills: '',
  });
  
  // Activity state
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Phone verification state
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Email verification state
  const [resendingEmailVerification, setResendingEmailVerification] = useState(false);

  // Personal workspace creation state (Spec P3.3 - profile-driven creation
  // path for invited users who own no organization of their own)
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  // Load activities when activity tab is selected
  useEffect(() => {
    if (activeTab === 'activity' && activities.length === 0) {
      loadActivities();
    }
  }, [activeTab]);

  const loadActivities = async () => {
    try {
      setActivityLoading(true);
      const response = await authAPI.getUserActivities({ limit: 20 });
      // Transform activities to match UserActivity interface
      const transformedActivities: UserActivity[] = response.activities.map(activity => ({
        ...activity,
        user_id: activity.id, // Use activity id as user_id for now
        activity_metadata: {
          service: 'auth',
          category: 'security'
        }
      }));
      setActivities(transformedActivities);
    } catch (err) {
      logger.error('Error loading activities:', err);
      showError('Failed to load activities');
    } finally {
      setActivityLoading(false);
    }
  };

  const formatActivityDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 168) {
      const days = Math.floor(diffInHours / 24);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the new auth API to get current user
      const userDetails = await authAPI.me();
      
      // Transform the API response to match the enhanced profile structure
      const transformedData: ProfileData = {
        id: userDetails.id,
        email: userDetails.email,
        personal_info: {
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          full_name: userDetails.full_name || `${userDetails.first_name} ${userDetails.last_name}`,
          avatar: userDetails.avatar,
          bio: 'No bio available', // Not available in current API
          location: 'No location available', // Not available in current API
          phone: userDetails.phone_number || '',
          phone_verified: userDetails.phone_verified || false,
          timezone: userDetails.timezone || 'UTC',
        },
        professional_info: {
          job_title: 'No job title available', // Not available in current API
          company: 'No company available', // Not available in current API
          industry: 'No industry available', // Not available in current API
          experience_level: 'No experience level available', // Not available in current API
          skills: [], // Not available in current API
          certifications: [], // Not available in current API
        },
        social_links: {
          linkedin: undefined, // Not available in current API
          twitter: undefined, // Not available in current API
          website: undefined, // Not available in current API
          github: undefined, // Not available in current API
        },
        preferences: {
          language: userDetails.preferences?.language || 'en',
          date_format: 'YYYY-MM-DD', // Default value
          time_format: '24h', // Default value
          theme: userDetails.preferences?.theme || 'light',
        },
        privacy: {
          profile_visibility: 'team', // Default value
          allow_marketing_emails: userDetails.preferences?.notifications || false,
          allow_data_sharing: true, // Default value
        },
        account_info: {
          is_active: userDetails.is_active || true,
          is_verified: userDetails.is_verified || userDetails.emailVerified || false,
          last_login: userDetails.last_login || '',
          created_at: userDetails.created_at || userDetails.createdAt,
          updated_at: userDetails.updated_at || '',
        },
        organizations: userDetails.organizations || [],
      };
      
      // Initialize form data with current values
      setFormData({
        first_name: userDetails.first_name || '',
        last_name: userDetails.last_name || '',
        phone_number: userDetails.phone_number || '',
        timezone: userDetails.timezone || 'UTC',
        bio: '',
        location: '',
        job_title: '',
        company: '',
        industry: '',
        experience_level: '',
        skills: '',
      });
      
      setProfileData(transformedData);
    } catch (err) {
      logger.error('Error loading profile:', err);
      setError('Failed to load profile data');
      showError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  /* const handleSuccess = (message: string) => { // Not used
    showSuccess(message);
    // Reload profile data after successful update
    loadProfileData();
  }; */

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);

      // Prepare update data, only include phone_number if it's not empty
      const updateData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        timezone: formData.timezone,
      };

      // Only include phone_number if it's provided and not empty
      if (formData.phone_number && formData.phone_number.trim()) {
        updateData.phone_number = formData.phone_number.trim();
      }

      // Update profile with the new data
      await authAPI.updateProfile(updateData);

      showSuccess('Profile saved successfully!');
      setEditMode(false);
      
      // Reload profile data to reflect changes
      await loadProfileData();
    } catch (err: any) {
      logger.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile changes');
      showError(err.message || 'Failed to save profile changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: keyof EditFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePhoneNumber = (phone: string): string | null => {
    if (!phone.trim()) return null; // Empty is OK
    
    // Check if it starts with + and has at least 10 digits
    const phoneRegex = /^\+\d{10,}$/;
    if (!phoneRegex.test(phone)) {
      return 'Phone number must be in international format (e.g., +1234567890)';
    }
    
    return null;
  };

  const getPhoneValidationError = (): string | null => {
    return validatePhoneNumber(formData.phone_number);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset form data to original values
    if (profileData) {
      setFormData({
        first_name: profileData.personal_info.first_name,
        last_name: profileData.personal_info.last_name,
        phone_number: profileData.personal_info.phone || '',
        timezone: profileData.personal_info.timezone,
        bio: profileData.personal_info.bio || '',
        location: profileData.personal_info.location || '',
        job_title: profileData.professional_info.job_title || '',
        company: profileData.professional_info.company || '',
        industry: profileData.professional_info.industry || '',
        experience_level: profileData.professional_info.experience_level || '',
        skills: profileData.professional_info.skills?.join(', ') || '',
      });
    }
  };

  const handleUpdatePhoneNumber = async (phoneNumber: string) => {
    try {
      setSaving(true);
      setError(null);

      // Validate phone number format
      const validationError = validatePhoneNumber(phoneNumber);
      if (validationError) {
        setError(validationError);
        showError(validationError);
        return;
      }

      // Update phone number
      await authAPI.updatePhoneNumber(phoneNumber);
      showSuccess('Phone number updated successfully. Please verify your new number.');
      
      // Reload profile data to reflect changes
      await loadProfileData();
    } catch (err: any) {
      logger.error('Error updating phone number:', err);
      setError(err.message || 'Failed to update phone number');
      showError(err.message || 'Failed to update phone number');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestPhoneVerification = async () => {
    try {
      setVerificationLoading(true);
      setError(null);

      // If there's a phone number in the form that's different from the saved one, update it first
      if (formData.phone_number && formData.phone_number !== profileData?.personal_info.phone) {
        await handleUpdatePhoneNumber(formData.phone_number);
      }

      const response = await authAPI.requestPhoneVerification();
      showSuccess(response.message || 'Verification code sent to your phone');
      setShowPhoneVerification(true);

      // In development, show the verification code
      if (response.verification_code) {
        showSuccess(`Verification code: ${response.verification_code}`);
      }
    } catch (err: any) {
      logger.error('Error requesting phone verification:', err);
      setError(err.message || 'Failed to send verification code');
      showError(err.message || 'Failed to send verification code');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    try {
      setVerificationLoading(true);
      setError(null);

      if (!verificationCode.trim()) {
        setError('Please enter the verification code');
        showError('Please enter the verification code');
        return;
      }

      const response = await authAPI.verifyPhoneNumber(verificationCode);
      showSuccess(response.message || 'Phone number verified successfully');

      setShowPhoneVerification(false);
      setVerificationCode('');

      // Reload profile data to update verification status
      await loadProfileData();
    } catch (err: any) {
      logger.error('Error verifying phone:', err);
      setError(err.message || 'Failed to verify phone number');
      showError(err.message || 'Failed to verify phone number');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendEmailVerification = async () => {
    try {
      setResendingEmailVerification(true);
      setError(null);

      // Get email from profile data (it's at the top level, not in personal_info)
      const email = profileData?.email;
      if (!email) {
        showError('Email not found');
        return;
      }

      await authAPI.resendVerification(email);
      showSuccess('Verification email sent! Please check your inbox.');
    } catch (err: any) {
      logger.error('Error resending email verification:', err);
      setError(err.message || 'Failed to send verification email');
      showError(err.message || 'Failed to send verification email');
    } finally {
      setResendingEmailVerification(false);
    }
  };

  // /auth/me returns `organizations` as a list of memberships, each nesting
  // the organization under `.organization` (see UserOrganizationResponse /
  // OrganizationResponse in services/auth/auth_models.py - `is_personal`
  // lives on the nested object, not flat on the membership). Some call
  // sites/tests hand this function a flattened shape instead, so check both
  // rather than assuming one serialization.
  const hasPersonalWorkspace = profileData?.organizations?.some(
    (o: any) => o?.is_personal ?? o?.organization?.is_personal
  );

  const handleCreatePersonalWorkspace = async () => {
    try {
      setCreatingWorkspace(true);
      await organizationAPI.createOrganization({
        name: `${profileData?.personal_info.first_name || 'My'}'s Workspace`,
        plan_type: 'free',
      });

      // Deliberately NOT switching into the new organization here. Do not
      // "helpfully" add that back - see AcceptInvitation.tsx's
      // handleAcceptInvitation for the same reasoning, verbatim:
      //   - OrganizationContext.switchOrganization is the only thing that
      //     correctly persists a switch (access_token, refresh_token,
      //     current_organization_id, enterprise_org_id and
      //     enterprise_user_role, together) - but it resolves the target
      //     org via organizations.find(o => o.id === orgId) against its
      //     already-loaded list, which cannot yet contain an org just
      //     created here. It throws "Organization not found".
      //   - The raw organizationAPI.switchOrganization call mints new
      //     org-scoped tokens but persists nothing - the response would be
      //     silently dropped, leaving the bearer token scoped to the OLD
      //     organization while the UI implies the new one. Worse than not
      //     switching at all.
      // So: write nothing, reload, and let the new workspace show up in the
      // org switcher, which the user can enter through the normal, correct
      // path.
      //
      // No success toast either, deliberately - AcceptInvitation.tsx calls
      // none before its own post-mutation reload, for a reason that applies
      // identically here: showSuccess() only enqueues a React state update
      // (useToast's addToast -> setToasts), and nothing yields to a
      // render/paint cycle between that call and window.location.reload()
      // on the next line - the page unloads before React ever gets to show
      // it. OrganizationContext.switchOrganization's setTimeout(..., 100)
      // is not a counterexample: that delay orders a localStorage write,
      // not human legibility, and 100ms is far too short to read a toast
      // regardless. The button's own loading state covers "is this
      // working", and the reload - during which the CTA disappears once
      // the new org is picked up - covers "did this work".
      window.location.reload();
    } catch (err: any) {
      showError(err.message || 'Failed to create workspace');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const renderOverviewTab = () => (
    <div className={styles.overviewContent}>
      <div className={styles.profileHeader}>
        <div className={styles.avatar}>
          {profileData?.personal_info.avatar ? (
            <img src={profileData.personal_info.avatar} alt="Profile" />
          ) : (
            <FiUser />
          )}
        </div>
        <div className={styles.profileInfo}>
          <h2>{profileData?.personal_info.full_name || 'Unknown User'}</h2>
          <p className={styles.email}>{profileData?.email}</p>
          <p className={styles.jobTitle}>{profileData?.professional_info.job_title || 'No job title'}</p>
          <p className={styles.location}>{profileData?.personal_info.location || 'No location'}</p>
        </div>
        <div className={styles.profileActions}>
          <Button
            onClick={editMode ? handleCancelEdit : () => setEditMode(true)}
            variant={editMode ? 'secondary' : 'primary'}
            icon={editMode ? <FiX /> : <FiEdit />}
            disabled={saving}
          >
            {editMode ? 'Cancel' : 'Edit Profile'}
          </Button>
          {editMode && (
            <Button
              onClick={handleSaveProfile}
              variant="primary"
              icon={<FiSave />}
              loading={saving}
              disabled={saving || !!getPhoneValidationError()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      </div>

      {!hasPersonalWorkspace && (
        <div className={styles.workspaceCta}>
          <p>
            You don't have a personal workspace yet. Creating one is free and
            doesn't affect the organizations you've been invited to.
          </p>
          <Button
            variant="primary"
            onClick={handleCreatePersonalWorkspace}
            loading={creatingWorkspace}
            disabled={creatingWorkspace}
          >
            {creatingWorkspace ? 'Creating…' : 'Create personal workspace'}
          </Button>
        </div>
      )}

      <div className={styles.infoGrid}>
        <Card className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <h3>Personal Information</h3>
            {editMode && <span className={styles.editingBadge}>Editing</span>}
          </div>
          
          {editMode ? (
            <div className={styles.editForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>First Name</label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleFormChange('first_name', e.target.value)}
                    placeholder="Enter first name"
                    className={styles.formInput}
                    disabled={saving}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Last Name</label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleFormChange('last_name', e.target.value)}
                    placeholder="Enter last name"
                    className={styles.formInput}
                    disabled={saving}
                  />
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label>Phone Number</label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => handleFormChange('phone_number', e.target.value)}
                  placeholder="Enter phone number (e.g., +1234567890)"
                  className={styles.formInput}
                  disabled={saving}
                />
                {getPhoneValidationError() && (
                  <div className={styles.fieldError}>
                    {getPhoneValidationError()}
                  </div>
                )}
                {profileData?.personal_info.phone_verified ? (
                  <div className={styles.phoneVerified}>
                    <FiPhone className={styles.verifiedIcon} />
                    <span>Phone number verified</span>
                  </div>
                ) : profileData?.personal_info.phone ? (
                  <div className={styles.phoneUnverified}>
                    <Button
                      onClick={handleRequestPhoneVerification}
                      variant="secondary"
                      size="sm"
                      loading={verificationLoading}
                      disabled={verificationLoading}
                    >
                      Verify Phone
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className={styles.formGroup}>
                <label>Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => handleFormChange('timezone', e.target.value)}
                  className={styles.formSelect}
                  disabled={saving}
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="Europe/London">London</option>
                  <option value="Europe/Paris">Paris</option>
                  <option value="Asia/Tokyo">Tokyo</option>
                  <option value="Asia/Shanghai">Shanghai</option>
                  <option value="Australia/Sydney">Sydney</option>
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label>Location</label>
                <Input
                  value={formData.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  placeholder="Enter location"
                  className={styles.formInput}
                  disabled={saving}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleFormChange('bio', e.target.value)}
                  placeholder="Tell us about yourself..."
                  className={styles.formTextarea}
                  rows={3}
                  disabled={saving}
                />
              </div>
            </div>
          ) : (
            <div className={styles.infoDisplay}>
              <div className={styles.infoItem}>
                <label>Full Name:</label>
                <span>{profileData?.personal_info.full_name || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Email:</label>
                <span>{profileData?.email}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Phone:</label>
                <div className={styles.phoneInfo}>
                  <span>{profileData?.personal_info.phone || 'Not set'}</span>
                  {profileData?.personal_info.phone && (
                    <div className={styles.phoneStatus}>
                      <Badge 
                        variant={profileData.personal_info.phone_verified ? 'success' : 'warning'}
                        size="sm"
                      >
                        {profileData.personal_info.phone_verified ? 'Verified' : 'Not Verified'}
                      </Badge>
                      {!profileData.personal_info.phone_verified && (
                        <Button
                          onClick={handleRequestPhoneVerification}
                          variant="secondary"
                          size="sm"
                          loading={verificationLoading}
                          disabled={verificationLoading}
                          className={styles.verifyButton}
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.infoItem}>
                <label>Location:</label>
                <span>{profileData?.personal_info.location || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Timezone:</label>
                <span>{profileData?.personal_info.timezone || 'UTC'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Bio:</label>
                <span>{profileData?.personal_info.bio || 'No bio'}</span>
              </div>
            </div>
          )}
        </Card>

        <Card className={styles.infoCard}>
          <div className={styles.cardHeader}>
            <h3>Professional Information</h3>
            {editMode && <span className={styles.editingBadge}>Editing</span>}
          </div>
          
          {editMode ? (
            <div className={styles.editForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Job Title</label>
                  <Input
                    value={profileData?.professional_info.job_title || ''}
                    placeholder="Enter job title"
                    className={styles.formInput}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Company</label>
                  <Input
                    value={profileData?.professional_info.company || ''}
                    placeholder="Enter company name"
                    className={styles.formInput}
                  />
                </div>
              </div>
              
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  <Input
                    value={profileData?.professional_info.industry || ''}
                    placeholder="Enter industry"
                    className={styles.formInput}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Experience Level</label>
                  <select className={styles.formSelect}>
                    <option value="">Select experience level</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="lead">Lead</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label>Skills (comma separated)</label>
                <Input
                  value={profileData?.professional_info.skills?.join(', ') || ''}
                  placeholder="Enter skills separated by commas"
                  className={styles.formInput}
                />
              </div>
            </div>
          ) : (
            <div className={styles.infoDisplay}>
              <div className={styles.infoItem}>
                <label>Job Title:</label>
                <span>{profileData?.professional_info.job_title || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Company:</label>
                <span>{profileData?.professional_info.company || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Industry:</label>
                <span>{profileData?.professional_info.industry || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Experience Level:</label>
                <span>{profileData?.professional_info.experience_level || 'Not set'}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Skills:</label>
                <span>{profileData?.professional_info.skills?.join(', ') || 'No skills listed'}</span>
              </div>
            </div>
          )}
        </Card>

        <Card className={styles.infoCard}>
          <h3>Account Information</h3>
          <div className={styles.infoItem}>
            <label>Account Status:</label>
            <span className={profileData?.account_info.is_active ? styles.active : styles.inactive}>
              {profileData?.account_info.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className={styles.infoItem}>
            <label>Email Verified:</label>
            <div className={styles.emailVerificationStatus}>
              <Badge
                variant={profileData?.account_info.is_verified ? 'success' : 'warning'}
                size="sm"
              >
                {profileData?.account_info.is_verified ? 'Verified' : 'Not Verified'}
              </Badge>
              {!profileData?.account_info.is_verified && (
                <Button
                  onClick={handleResendEmailVerification}
                  variant="secondary"
                  size="sm"
                  loading={resendingEmailVerification}
                  disabled={resendingEmailVerification}
                  className={styles.verifyButton}
                >
                  Resend Verification
                </Button>
              )}
            </div>
          </div>
          <div className={styles.infoItem}>
            <label>Last Login:</label>
            <span>{profileData?.account_info.last_login ? new Date(profileData.account_info.last_login).toLocaleString() : 'Never'}</span>
          </div>
          <div className={styles.infoItem}>
            <label>Member Since:</label>
            <span>{profileData?.account_info.created_at ? new Date(profileData.account_info.created_at).toLocaleDateString() : 'Unknown'}</span>
          </div>
        </Card>
      </div>
    </div>
  );



  const renderActivityTab = () => (
    <div className={styles.activityContent}>
      <div className={styles.activityHeader}>
        <div>
          <h2>Activity Timeline</h2>
          <p>Track your actions across all services</p>
        </div>
        <Button
          onClick={loadActivities}
          variant="secondary"
          size="sm"
          icon={<FiRefreshCw />}
          disabled={activityLoading}
        >
          Refresh
        </Button>
      </div>

      <Card className={styles.activitiesCard}>
        {activityLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <span>Loading activities...</span>
          </div>
        ) : activities.length === 0 ? (
          <div className={styles.emptyState}>
            <FiUser className={styles.emptyIcon} />
            <h3>No activities found</h3>
            <p>You haven't performed any tracked activities yet.</p>
          </div>
        ) : (
          <div className={styles.activitiesList}>
            {activities.map((activity) => {
              const category = getActivityCategory(activity.activity_type);
              const icon = getActivityIcon(activity.activity_type);
              const color = getActivityColor(activity.activity_type);
              
              return (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityIcon} style={{ backgroundColor: color }}>
                    {icon}
                  </div>
                  <div className={styles.activityContent}>
                    <div className={styles.activityHeader}>
                      <h4 className={styles.activityTitle}>{activity.activity_title}</h4>
                      <span className={styles.activityTime}>
                        {formatActivityDate(activity.created_at)}
                      </span>
                    </div>
                    {activity.activity_description && (
                      <p className={styles.activityDescription}>
                        {activity.activity_description}
                      </p>
                    )}
                    <div className={styles.activityMeta}>
                      <Badge variant="tertiary" size="sm">
                        {category}
                      </Badge>
                      {activity.activity_metadata.service && (
                        <Badge variant="secondary" size="sm">
                          {activity.activity_metadata.service}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading profile...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FiAlertCircle className={styles.errorIcon} />
        <h2>Error Loading Profile</h2>
        <p>{error}</p>
        <Button onClick={loadProfileData} variant="primary">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.profile}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Profile</h1>
          <p>Manage your account information and preferences</p>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FiUser />
            Overview
          </button>

          <button
            className={`${styles.tab} ${activeTab === 'activity' ? styles.active : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <FiActivity />
            Activity
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'activity' && renderActivityTab()}
        </div>
      </div>

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Verify Phone Number</h3>
              <Button
                onClick={() => setShowPhoneVerification(false)}
                variant="secondary"
                size="sm"
                icon={<FiX />}
                className={styles.closeButton}
              >
                Close
              </Button>
            </div>
            
            <div className={styles.modalBody}>
              <p>Enter the verification code sent to your phone number:</p>
              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter verification code"
                className={styles.verificationInput}
                disabled={verificationLoading}
              />
            </div>
            
            <div className={styles.modalActions}>
              <Button
                onClick={() => setShowPhoneVerification(false)}
                variant="secondary"
                disabled={verificationLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerifyPhone}
                variant="primary"
                loading={verificationLoading}
                disabled={verificationLoading || !verificationCode.trim()}
              >
                {verificationLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 